import type { ConversationAnchorType, ConversationThreadView } from '@scoutaround/script-breakdown-shared';

import { Icon, type IconName } from '../Icon.js';
import '../../styles/conversations.css';

const STATE_NOTE: Readonly<Record<string, string>> = Object.freeze({
  REMOVED: 'no longer in the production',
  EARLIER_DRAFT: 'earlier draft',
});

/** What a conversation is about, as the icon the rest of the workspace uses for it. */
const ANCHOR_ICON: Readonly<Record<ConversationAnchorType, IconName>> = Object.freeze({
  PROJECT: 'checklist',
  SCENE: 'scenes',
  REQUIREMENT: 'pin',
  PLACE: 'map',
  ELEMENT: 'model',
  REFERENCE: 'image',
});

function shortWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Conversations as compact rows: the title, what it is about (when the list
 * spans anchors), private or resolved, when it last moved, and what is new for
 * the reader — a count for one they follow, a dot for one they can read but do
 * not follow (spec §3). Unread rows are bold; nothing is coloured for it.
 */
export function ThreadList({
  threads,
  showAnchor,
  activeId = null,
  testId,
  emptyText,
  onOpen,
}: {
  threads: readonly ConversationThreadView[];
  showAnchor: boolean;
  activeId?: string | null;
  testId: string;
  emptyText: string;
  onOpen: (threadId: string) => void;
}) {
  if (threads.length === 0) {
    return (
      <p className="mm cv-list-empty" data-testid={`${testId}-empty`}>
        {emptyText}
      </p>
    );
  }

  return (
    <ul data-testid={testId} className="cv-list">
      {threads.map((t) => {
        const unread = t.me.unread;
        const fresh = unread > 0 || t.me.hasNew;
        const note = STATE_NOTE[t.anchorState];
        return (
          <li key={t.id}>
            <button
              type="button"
              className="cv-row"
              data-testid={`conversation-row-${t.id}`}
              data-fresh={fresh ? '1' : undefined}
              aria-current={activeId === t.id ? 'true' : undefined}
              onClick={() => {
                onOpen(t.id);
              }}
            >
              <span className="cv-row-top">
                <span className="cv-row-title">{t.title ?? 'Title removed by ScoutAround'}</span>
                <span className="cv-row-when">{shortWhen(t.lastMessageAt)}</span>
              </span>
              <span className="cv-row-bottom">
                <span className="cv-row-meta">
                  {showAnchor ? (
                    <span className="cv-chip" title={t.anchorType === 'PROJECT' ? 'Whole production' : t.anchorLabel}>
                      <Icon name={ANCHOR_ICON[t.anchorType]} size={12} />
                      <span>{t.anchorType === 'PROJECT' ? 'Whole production' : t.anchorLabel}</span>
                    </span>
                  ) : null}
                  {note ? <span className="cv-row-note">{note}</span> : null}
                  {t.visibility === 'PRIVATE' ? <span>Private</span> : null}
                  {t.status === 'RESOLVED' ? <span>Resolved</span> : null}
                </span>
                <span className="cv-row-badges">
                  {t.me.mentioned ? (
                    <span data-testid={`conversation-row-mention-${t.id}`} className="cv-mention" aria-label="You were mentioned">
                      @
                    </span>
                  ) : null}
                  {unread > 0 ? (
                    <span data-testid={`conversation-row-unread-${t.id}`} className="cv-unread" aria-label={`${String(unread)} unread`}>
                      {unread}
                    </span>
                  ) : fresh ? (
                    <span className="cv-new" aria-label="Something new" />
                  ) : null}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
